import type { ReactNode } from 'react'

import { cn } from './cn'

export interface RadioProps {
  readonly checked: boolean
  /** Called when the radio is chosen. */
  readonly onChange: () => void
  /** The group's name, shared by every radio in it. */
  readonly name: string
  readonly label: ReactNode
  /** Names the radio in place of its label, when the label alone does not say which one it is. */
  readonly 'aria-label'?: string
  /** Draws the circle in the error color, as one of a group whose error `describedBy` names; a radio has no invalid state of its own. */
  readonly invalid?: boolean
  /** The id of the group's error or hint, read with the radio. */
  readonly describedBy?: string | undefined
  readonly className?: string
}

/**
 * A real radio, visually hidden, behind a drawn 16 px circle, as the mockups' radios look. Its
 * label is the target, at least 24 by 24 px. The circle shows the input's keyboard focus.
 */
export function Radio(props: RadioProps) {
  return (
    <label className={cn('inline-flex min-h-6 min-w-6 cursor-pointer items-center gap-1.5 text-sm text-text-primary', props.className)}>
      <input
        type="radio"
        name={props.name}
        checked={props.checked}
        onChange={props.onChange}
        aria-label={props['aria-label']}
        aria-describedby={props.describedBy}
        className="peer sr-only"
      />
      <span
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-full border bg-background transition',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-main',
          props.checked ? 'border-primary-main' : props.invalid === true ? 'border-error-main' : 'border-text-light',
        )}
      >
        {/* A border, not only a fill, so the dot still shows in forced colors. */}
        {props.checked && <span className="size-2 rounded-full border-4 border-primary-main bg-primary-main" />}
      </span>
      {props.label}
    </label>
  )
}
